import * as log from '../util/log';
import { storableError } from '../util/errors';
import { clearCurrentUser, fetchCurrentUser, updatedVisitorData } from './user.duck';
import {
  createUserWithIdp,
  findYouthAccountApi,
  validateYouthAccount,
  algoliaCRUDApis,
  upsertUserDetailsApi,
  verifyJwtToken,
  generateJwtToken,
} from '../util/api';
import { createNewCircleUser } from '../containers/CircleCommunityPage/CircleCommunityPage.duck';
import { LOCAL_STORAGE_CIRCLE_AUTH_TOKEN, YOUTH_INVITE_STATUS, CREATE_ALGOLIA_TYPE, CURRENT_USER_INDIVIDUAL_TYPE, USER_TYPE_YOUTH, YOUTH_AGE_LIMIT } from '../util/types';
import { types } from '../util/sdkLoader';
import { updateProfile } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { makeCreateUpdateAlgoliaUserData } from '../util/data';

const authenticated = authInfo => authInfo?.isAnonymous === false;
const loggedInAs = authInfo => authInfo?.isLoggedInAs === true;
const { UUID } = types
const indexName = process.env.REACT_APP_ALGOLIA_USERS;
// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

export const YOUTH_ACCOUNT_INVITE_REQUEST = 'app/auth/YOUTH_ACCOUNT_INVITE_REQUEST';
export const YOUTH_ACCOUNT_INVITE_SUCCESS = 'app/auth/YOUTH_ACCOUNT_INVITE_SUCCESS';
export const YOUTH_ACCOUNT_INVITE_ERROR = 'app/auth/YOUTH_ACCOUNT_INVITE_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

export const RESTRICT_LOGIN_SUCCESS = 'app/auth/RESTRICT_LOGIN_SUCCESS';
export const SET_SHOPIFY_REDIRECT_URL = 'app/auth/SET_SHOPIFY_REDIRECT_URL';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // is marketplace operator logged in as a marketplace user
  isLoggedInAs: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,

  // restrict youth to login
  restrictUser: false,

  // restrict youth to signup
  restrictUserToSignup: false,

  // youth account invite
  youthAccountInviteError: null,
  youthAccountInviteInProgress: false,
  youthAccountInviteSuccess: null,

  // Shopify handoff
  shopifyRedirectUrl: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        isLoggedInAs: loggedInAs(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
        restrictUserToSignup: false,
        restrictUser: false
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return {
        ...state,
        logoutInProgress: false,
        isAuthenticated: false,
        isLoggedInAs: false,
        authScopes: [],
        restrictUserToSignup: false
      };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null, restrictUserToSignup: false, restrictUser: false };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    case YOUTH_ACCOUNT_INVITE_REQUEST:
      return {
        ...state,
        youthAccountInviteError: null,
        youthAccountInviteInProgress: true,
        youthAccountInviteSuccess: null,

      };
    case YOUTH_ACCOUNT_INVITE_SUCCESS:
      return {
        ...state,
        youthAccountInviteInProgress: false,
        youthAccountInviteSuccess: payload,
        youthAccountInviteError: null,
      };
    case YOUTH_ACCOUNT_INVITE_ERROR:
      return {
        ...state,
        youthAccountInviteInProgress: false,
        youthAccountInviteError: payload,
        youthAccountInviteSuccess: null,
      };

    case RESTRICT_LOGIN_SUCCESS:
      return {
        ...state,
        loginInProgress: false,
        restrictUser: true,
        signupInProgress: false,
        restrictUserToSignup: true,
      };

    case SET_SHOPIFY_REDIRECT_URL:
      return {
        ...state,
        shopifyRedirectUrl: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });


export const checkValidYouthInviteRequest = () => ({ type: YOUTH_ACCOUNT_INVITE_REQUEST });
export const checkValidYouthInviteSuccess = (payload) => ({ type: YOUTH_ACCOUNT_INVITE_SUCCESS, payload });
export const checkValidYouthInviteError = error => ({ type: YOUTH_ACCOUNT_INVITE_ERROR, payload: error, });

export const userLogout = () => ({ type: USER_LOGOUT });
export const restrictLoginSuccess = () => ({ type: RESTRICT_LOGIN_SUCCESS });
export const setShopifyRedirectUrl = url => ({ type: SET_SHOPIFY_REDIRECT_URL, payload: url });

// Helper function to create profile listing after signup
const createProfileListing = (params) => (dispatch, getState, sdk) => {
  const currentUser = getState().user.currentUser;
  const currentUserId = currentUser?.id?.uuid || '';
  const ProfileListing = {
    title:
      params?.firstName && params?.lastName
        ? `${params.firstName} ${params.lastName}`
        : 'ProfileListing',
    description: 'Profilelisting',
    publicData: {
      authorId: currentUserId,
      listingType: 'profile_listing',
      transactionProcessAlias: 'default-inquiry/release-1',
      unitType: 'hour',
      isProfileListing: true,
    },
  };

  return sdk.ownListings.create(ProfileListing).then(response => {
    if (response) {
      const updatedValues = {
        publicData: {
          profileListingId: response?.data?.data?.id?.uuid,
        },
      };
      return dispatch(updateProfile(updatedValues));
    }
    return null;
  });
};

// Helper function to update user in Algolia after signup
const updateUserInAlgolia = (params) => (dispatch, getState, sdk) => {
  const currentUser = getState().user.currentUser;
  // Set state to "inactive" for non-individual users, otherwise use default state
  const updateParams ={ state: "invalid" };

  // Create user data for Algolia using the same pattern as listings
  const userData = makeCreateUpdateAlgoliaUserData(indexName, currentUser, params, updateParams);

  // Use the same API pattern as listings
  return algoliaCRUDApis(CREATE_ALGOLIA_TYPE, userData)
    .then((response) => {
      console.log('User successfully created in Algolia:', response);
      return response;
    })
    .catch(e => {
      console.error('Error creating user in Algolia:', e);
      return e;
    });
};
// ================ Thunks ================ //

const removeQueryParamFromUrl = paramName => {
  if (typeof window === 'undefined') {
    return;
  }

  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.delete(paramName);
  const newSearch = currentUrl.searchParams.toString();
  const cleanedUrl = `${currentUrl.pathname}${newSearch ? `?${newSearch}` : ''}${currentUrl.hash}`;
  window.history.replaceState({}, document.title, cleanedUrl);
};

export const saveShopifyDetails = search => async (dispatch, getState) => {
  if (typeof window === 'undefined') {
    console.log('no window');
    return null;
  }

  const searchParams = new URLSearchParams(search || window.location.search);
  const shopifyToken = searchParams.get('shopify_token');
  if (!shopifyToken) {
    console.log('no shopify token');
    return null;
  }

  const currentUser = getState()?.user?.currentUser;
  console.log('currentUser', currentUser);
  const currentUserId = currentUser?.id?.uuid;
  const publicData = currentUser?.attributes?.profile?.publicData || {};
  const isConnected = publicData?.shopifyStatus === 'CONNECTED';

  if (!currentUserId || isConnected) {
    console.log('no user or already connected', currentUserId, isConnected);
    return null;
  }

  try {
    const verificationResponse = await verifyJwtToken({ token: shopifyToken });
    const decoded = verificationResponse?.decoded || {};
    if (!verificationResponse?.valid || !decoded) {
      console.log('invalid token');
      return null;
    }

    console.log('decoded', decoded);
    const normalizedShopifyData = {
      shopifyStatus: 'DISCONNECTED',
      shopifyId: decoded.shopifyId ?? null,
      myShopifyDomain: decoded.myShopifyDomain ?? null,
    };

    console.log('normalizedShopifyData', normalizedShopifyData);

    await dispatch(
      updateProfile({
        publicData: {
          ...normalizedShopifyData,
        },
      })
    );
    removeQueryParamFromUrl('shopify_token');

    const myShopifyDomain = normalizedShopifyData?.myShopifyDomain;
    const appName = process.env.REACT_APP_SHOPIFY_APP_NAME;
    const storeName = myShopifyDomain?.split('.')?.[0];

    if (!storeName || !appName) {
      console.log('either storeName or appName is missing', {storeName, appName})
      return null;
    }

    const generatedTokenResponse = await generateJwtToken({ userId: currentUserId });
    const generatedToken = generatedTokenResponse?.token;

    console.log('generatedToken', generatedToken);
    if (!generatedToken) {
      return null;
    }


    const shopifyUrl = `https://admin.shopify.com/store/${storeName}/apps/${appName}/app?token=${encodeURIComponent(
      generatedToken
    )}`;
    console.log('shopifyUrl', shopifyUrl)
    dispatch(setShopifyRedirectUrl(shopifyUrl));
    return shopifyUrl;
  } catch (error) {
    console.error('Failed to process Shopify token handoff:', error);
    return null;
  }
};

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password, config, invite_id, isFirstLogin, search) => (
  dispatch,
  getState,
  sdk
) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());
  const searchToProcess =
    typeof search === 'string'
      ? search
      : typeof window !== 'undefined'
      ? window.location.search
      : '';

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return validateYouthAccount({ email: username })
    .then(res => {
      if (res.accountActive) {
        return sdk
          .login({ username, password })
          .then(() => dispatch(fetchCurrentUser({ afterLogin: true }, { invite_id, isFirstLogin })))
          .then(() => dispatch(saveShopifyDetails(searchToProcess)))
          .then(() => dispatch(loginSuccess()))
          .then(() => dispatch(updatedVisitorData(config)))
          .catch(e => dispatch(loginError(storableError(e))));
      } else {
        dispatch(restrictLoginSuccess());
      }
    })
    .catch(error => {
      dispatch(loginError(storableError(error)));
    });
};

export const clearCircleAuthToken = () => {
  if (typeof window === 'undefined') return;

  // Remove the token from localStorage
  localStorage.removeItem(LOCAL_STORAGE_CIRCLE_AUTH_TOKEN);
  console.log('Circle auth token cleared');
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      clearCircleAuthToken();
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const fetchYouthAccountStatus = (params) => (dispatch, getState, sdk) => {
  const { email, publicData } = params || {};
  const { parentId, dob } = publicData || {};

  if (new Date().getFullYear() < Number(dob) + YOUTH_AGE_LIMIT && !parentId) return Promise.resolve(false);
  if (!parentId) return Promise.resolve(true);

  return findYouthAccountApi({
    pageParams: {
      page: 1,
      perPage: 10
    },
    payload: {
      guardian_id: parentId,
      youth_status: { $in: [YOUTH_INVITE_STATUS.INVITE, YOUTH_INVITE_STATUS.ACCEPT] }
    }
  })
    .then((res) => {
      return res.data.length;
    })
    .catch((error) => {
      console.error("Error fetching youth account status:", error);
      return false;
    });
};

export const signup = (params, config) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  const { invite_id } = params?.publicData || {};
  // Note: params are already structured on AuthenticationPage (handleSubmitSignup)

  // We must login the user if signup succeeds since the API doesn't
  // do that automatically.
  return dispatch(fetchYouthAccountStatus(params))
    .then((isYouthAllowed) => {
      if (!isYouthAllowed) {
        dispatch(restrictLoginSuccess());
        return Promise.resolve();
      }
      return sdk.currentUser
        .create(params)
        .then((response) => {
          dispatch(signupSuccess())

          // Save user details to MongoDB (non-blocking)
          const userId = response?.data?.data?.id?.uuid;
          const phoneNumber = params.protectedData?.phoneNumber;

          if (userId && phoneNumber) {
            upsertUserDetailsApi({ userId, phoneNumber }).catch(err => {
              console.error('Failed to save user details to MongoDB on signup:', err);
            });
          }

          return response
        })
        .then(async response => {
          await dispatch(login(params.email, params.password, config, invite_id, true));

          const userId = response?.data?.data?.id?.uuid;
          if (userId) {
            await dispatch(
              createNewCircleUser({
                password: params.password,
              })
            );
          }
        })
        .then(() => {
          return Promise.all([
            // dispatch(createProfileListing(params)),
            dispatch(updateUserInAlgolia({}))
          ]);
        })
        .catch(e => {
          dispatch(signupError(storableError(e)));
          log.error(e, 'signup-failed', {
            email: params.email,
            firstName: params.firstName,
            lastName: params.lastName,
          });
        });
    });
  // return sdk.currentUser
  //   .create(params)
  //   .then(() => dispatch(signupSuccess()))
  //   .then(_ =>
  //     Promise.all([
  //       dispatch(
  //         createNewCircleUser({
  //           email: params.email,
  //           password: params.password,
  //           name: params.firstName + ' ' + params.lastName[0],
  //         })
  //       ),
  //       dispatch(login(params.email, params.password, config, invite_id)),
  //     ])
  //   )
  //   .catch(e => {
  //     dispatch(signupError(storableError(e)));
  //     log.error(e, 'signup-failed', {
  //       email: params.email,
  //       firstName: params.firstName,
  //       lastName: params.lastName,
  //     });
  //   });
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  return createUserWithIdp(params)
    .then(res => {
      return dispatch(confirmSuccess());
    })
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};

export const checkValidYouthInvite = params => (dispatch, getState, sdk) => {
  try {
    dispatch(checkValidYouthInviteRequest());
    return findYouthAccountApi(params)
      .then(res => {
        console.log(res, '&&& &&& => res');
        return dispatch(checkValidYouthInviteSuccess(res.data[0]));
      })
      .catch(e => {
        log.error(e, 'create-user-with-idp-failed', { params });
        return dispatch(checkValidYouthInviteError(storableError(e)));
      });
  } catch (e) {
    console.error('Error in checkValidYouthInvite:', e);
    dispatch(checkValidYouthInviteError(storableError(e)))
    return Promise.reject(storableError(e));
  }
};

export const isAuthenticatedSelector = state => state.auth.isAuthenticated;
export const shopifyRedirectUrlSelector = state => state.auth.shopifyRedirectUrl;
